import type { UIMessage } from "ai";

/**
 * What the interviewer offered, read from its tool parts, and what the person
 * sends back. The app renders the tools' outputs, which the API has already
 * cut to fit (api/src/ai/cards.ts); legacy parts from before the answer card
 * and block mock-ups still render.
 */

export type Move = "example" | "challenge" | "options" | "number" | "case" | "missing";

export interface Card {
  options: string[];
  pick: "one" | "many";
  /** Whether the "add a word" line is shown. */
  note: boolean;
  moves: Move[];
}

export type MockupBlock =
  | { type: "table"; title?: string; columns: string[]; rows: string[][] }
  | { type: "fields"; title?: string; fields: Array<{ label: string; example: string }> }
  | { type: "message"; channel: "email" | "whatsapp" | "sms" | "screen"; to: string[]; subject?: string; text: string }
  | { type: "figures"; title?: string; items: Array<{ label: string; value: string }> };

export interface MockupView {
  toolCallId: string;
  title: string;
  blocks: MockupBlock[];
  markdown: string | null;
}

export interface Reactions {
  keep: string[];
  drop: string[];
  wrong: Array<{ target: string; note: string }>;
  add: Array<{ block: string; note: string }>;
}

// Matches the API's limit on `add` entries in a data-mockup part (more is a 400).
export const MAX_ADDS = 10;

export const EMPTY_REACTIONS: Reactions = { keep: [], drop: [], wrong: [], add: [] };

type ToolPart = { type: string; toolCallId: string; state: string; input?: unknown; output?: unknown };
const toolParts = (m: UIMessage) =>
  m.parts.filter((p): p is UIMessage["parts"][number] & ToolPart => typeof p.type === "string" && p.type.startsWith("tool-") && "toolCallId" in p);
const done = (p: ToolPart) => p.state === "output-available" && !!p.output && typeof p.output === "object" && !("error" in p.output);

/** The card under a message: the last valid offer in it, so a stray second offer never shows an earlier one. */
export function cardOf(message: UIMessage): Card | null {
  for (const p of toolParts(message).reverse()) {
    if (!done(p)) continue;
    if (p.type === "tool-offer") {
      const o = p.output as { replies: { options: string[]; pick: "one" | "many" } | null; moves: Move[] };
      if (o.replies) return { options: o.replies.options, pick: o.replies.pick, note: true, moves: o.moves ?? [] };
      if (o.moves?.length) return { options: [], pick: "one", note: false, moves: o.moves };
      continue;
    }
    if (p.type === "tool-suggest_replies") {
      const options = (p.output as { options?: unknown }).options;
      if (Array.isArray(options) && options.length) return { options: options.filter((o): o is string => typeof o === "string"), pick: "one", note: false, moves: [] };
    }
  }
  return null;
}

export function openingCard(chips: string[]): Card | null {
  return chips.length ? { options: chips, pick: "one", note: false, moves: [] } : null;
}

export function mockupsOf(message: UIMessage): MockupView[] {
  return toolParts(message)
    .filter((p) => p.type === "tool-show_mockup" && done(p))
    .map((p) => {
      const out = p.output as { title?: string; blocks?: MockupBlock[]; markdown?: string };
      const input = (p.input ?? {}) as { title?: string; markdown?: string };
      const blocks = Array.isArray(out.blocks) ? out.blocks : [];
      const markdown = blocks.length ? null : (out.markdown ?? input.markdown ?? null);
      return { toolCallId: p.toolCallId, title: out.title ?? input.title ?? "Mock-up", blocks, markdown };
    })
    .filter((v) => v.blocks.length > 0 || v.markdown);
}

export function composeAnswer(picked: string[], note: string): string {
  const n = note.trim();
  const p = picked.join(", ");
  return p && n ? `${p} — ${n}` : p || n;
}

export function blockName(b: MockupBlock): string {
  if ("title" in b && b.title) return b.title;
  return { table: "the table", fields: "the form", message: "the message", figures: "the figures" }[b.type];
}

export function composeReactions(title: string, r: Reactions, latest: boolean): string {
  const parts = [
    ...(r.keep.length ? [`keep ${r.keep.join(", ")}`] : []),
    ...(r.drop.length ? [`drop ${r.drop.join(", ")}`] : []),
    ...r.wrong.map((w) => `"${w.target}" is wrong: ${w.note}`),
    ...r.add.map((a) => `add to ${a.block}: ${a.note}`),
  ];
  if (parts.length === 0) return "";
  return `On the mock-up${latest ? "" : ` "${title}"`}: ${parts.join("; ")}.`;
}
